import React from 'react';
import PropTypes from 'prop-types';

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);

const EbitdaReconciliationTable = ({
  revenue,
  operatingExpenses,
  reportedEbitda,
  adjustments,
  adjustedEbitda,
  className,
}) => (
  <div className={`space-y-4 ${className || ''}`}>
    <div className="space-y-2">
      <div className="font-mono text-[11px] uppercase text-[hsl(var(--muted-foreground))] tracking-wide mb-3">
        Reported P&amp;L
      </div>
      <div className="space-y-1">
        <div className="flex items-center justify-between py-2 border-b border-[hsl(var(--border))]">
          <span className="text-[14px] text-[hsl(var(--foreground))]">Revenue</span>
          <span className="text-[14px] font-medium text-[hsl(var(--foreground))]">{formatCurrency(revenue)}</span>
        </div>
        <div className="flex items-center justify-between py-2 border-b border-[hsl(var(--border))]">
          <span className="text-[14px] text-[hsl(var(--foreground))]">Operating Expenses</span>
          <span className="text-[14px] font-medium text-[hsl(var(--foreground))]">({formatCurrency(operatingExpenses)})</span>
        </div>
        <div className="flex items-center justify-between py-2 border-b border-[hsl(var(--border))]">
          <span className="text-[14px] font-semibold text-[hsl(var(--foreground))]">Reported EBITDA</span>
          <span className="text-[14px] font-semibold text-[hsl(var(--foreground))]">{formatCurrency(reportedEbitda)}</span>
        </div>
      </div>
    </div>

    {adjustments.length > 0 && (
      <div className="space-y-2">
        <div className="font-mono text-[11px] uppercase text-[hsl(var(--muted-foreground))] tracking-wide mb-3">
          Adjustments
        </div>
        <div className="space-y-1">
          {adjustments.map((adjustment) => (
            <div key={adjustment.label} className="py-2 border-b border-[hsl(var(--border))]">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[14px] text-[hsl(var(--foreground))]">{adjustment.label}</span>
                <span className="text-[14px] font-medium text-[hsl(var(--succeedo-orange))]">
                  +{formatCurrency(adjustment.amount)}
                </span>
              </div>
              {adjustment.reason && (
                <div className="text-[12px] text-[hsl(var(--muted-foreground))] pl-4">{adjustment.reason}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    )}

    <div className="bg-[hsl(var(--succeedo-orange))]/5 border border-[hsl(var(--succeedo-orange))]/20 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <span className="text-[16px] font-semibold text-[hsl(var(--foreground))]">Adjusted EBITDA</span>
        <span className="text-[18px] font-bold text-[hsl(var(--succeedo-orange))]">
          {formatCurrency(adjustedEbitda)}
        </span>
      </div>
    </div>
  </div>
);

EbitdaReconciliationTable.propTypes = {
  revenue: PropTypes.number.isRequired,
  operatingExpenses: PropTypes.number.isRequired,
  reportedEbitda: PropTypes.number.isRequired,
  adjustments: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      amount: PropTypes.number.isRequired,
      reason: PropTypes.string,
    }),
  ).isRequired,
  adjustedEbitda: PropTypes.number.isRequired,
  className: PropTypes.string,
};

EbitdaReconciliationTable.defaultProps = {
  className: '',
};

export default EbitdaReconciliationTable;
